import React, { useCallback } from "react"
import { Audio, Sequence, useVideoConfig } from "remotion"
import { joinSplitPieces, msToFrames } from "../../utils/timing"
import type { Track } from "../../schemas"

interface VoiceoverTrackProps {
  track: Track
}

export const VoiceoverTrack: React.FC<VoiceoverTrackProps> = ({ track }) => {
  const { fps } = useVideoConfig()

  const segments = joinSplitPieces(track.sections.flatMap(s => s.segments))

  return (
    <>
      {segments.map((segment) => {
        const inFrame = msToFrames(segment.timeline.in, fps)
        const duration = msToFrames(segment.timeline.out - segment.timeline.in, fps)
        const content = segment.content
        if (!content?.mediaInfo?.originUrl) return null

        return (
          <Sequence key={segment.id} from={inFrame} durationInFrames={Math.max(duration, 1)}>
            <VoiceoverSegmentAudio
              segment={segment}
              durationInFrames={duration}
              fps={fps}
            />
          </Sequence>
        )
      })}
    </>
  )
}

const VoiceoverSegmentAudio: React.FC<{
  segment: Record<string, any>
  durationInFrames: number
  fps: number
}> = ({ segment, durationInFrames, fps }) => {
  const content = segment.content
  const audioSettings = content.audioSettings ?? {}
  const baseVolume = (audioSettings.volume ?? 100) / 100
  const fadeInFrames = msToFrames(audioSettings.fadeInMs ?? 200, fps)
  const fadeOutFrames = msToFrames(audioSettings.fadeOutMs ?? 200, fps)

  const volumeCallback = useCallback(
    (frame: number) => {
      let vol = baseVolume

      // Fade in
      if (frame < fadeInFrames && fadeInFrames > 0) {
        vol *= frame / fadeInFrames
      }

      // Fade out
      const fadeOutStart = durationInFrames - fadeOutFrames
      if (frame > fadeOutStart && fadeOutFrames > 0) {
        vol *= (durationInFrames - frame) / fadeOutFrames
      }

      return Math.max(0, Math.min(1, vol))
    },
    [baseVolume, fadeInFrames, fadeOutFrames, durationInFrames],
  )

  return (
    <Audio
      src={content.mediaInfo.originUrl}
      pauseWhenBuffering
      volume={volumeCallback}
      trimBefore={msToFrames(segment.trimStartMs ?? 0, fps) || undefined}
    />
  )
}
